import { APIContext } from "../../types";
import { makePath } from "../../../url";
import * as APITypes from "../../generated-types/v1/Api";
import { applyPagination } from "../../pagination";
import { getRaw, getJSON, postJSON } from "../../request";

const DEFAULT_QUERY_PARAMS: Partial<APITypes.RecordingListParams> = {
  pageSize: 25,
};

function applyOptions(
  params: URLSearchParams,
  options: APITypes.MeasurementQueryParams
) {
  if (options.metricName) {
    params.set("metricName", options.metricName);
  }

  if (options.sourceUri) {
    params.set("sourceUri", options.sourceUri);
  }

  if (options.timestampStart) {
    params.set("timestampStart", options.timestampStart);
  }

  if (options.timestampEnd) {
    params.set("timestampEnd", options.timestampEnd);
  }

  if (options.patientId) {
    params.set("patientId", options.patientId);
  }
}

export type MeasurementAPI = ReturnType<typeof createMeasurementsAPI>;

export function createMeasurementsAPI({
  buildURL,
  fetchRaw,
  fetchPagedJSON,
  fetchJSON,
}: APIContext) {
  function queryMeasurements(options: APITypes.MeasurementQueryParams) {
    const path = makePath(["api", "v1", "measurement", options.patientId]);

    const url = buildURL(path);

    const effectiveOptions = { ...DEFAULT_QUERY_PARAMS, ...options };

    applyPagination(url.searchParams, effectiveOptions);
    applyOptions(url.searchParams, effectiveOptions);

    const request = getJSON(url);
    return fetchPagedJSON<APITypes.Measurement>(request);
  }

  function fetchMeasurement({
    patientId,
    measurementId,
  }: {
    patientId: string;
    measurementId: string;
  }) {
    const path = makePath([
      "api",
      "v1",
      "measurement",
      patientId,
      measurementId,
    ]);
    const url = buildURL(path);
    const request = getJSON(url);
    return fetchJSON<APITypes.Measurement>(request);
  }

  function storeMeasurement({
    patientId,
    measurement,
  }: {
    patientId: string;
    measurement: APITypes.CreateMeasurementRequest;
  }) {
    const path = makePath(["api", "v1", "measurement", patientId]);
    const url = buildURL(path);
    const request = postJSON(url, measurement);
    return fetchJSON<APITypes.Measurement>(request);
  }

  function fetchMeasurementImage({
    patientId,
    measurementId,
  }: {
    patientId: string;
    measurementId: string;
  }) {
    const path = makePath([
      "api",
      "v1",
      "measurement",
      patientId,
      measurementId,
      "image",
    ]);

    const url = buildURL(path);
    const request = getRaw(url);
    return fetchRaw(request);
  }

  return {
    queryMeasurements,
    fetchMeasurement,
    storeMeasurement,
    fetchMeasurementImage,
  };
}
